import React, { useState, useEffect } from 'react';
import { useRouter, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.scss';
import UserProfileDropdown from '../UserProfileDropdown/UserProfileDropdown';
import {
  IconLayoutSidebarLeftCollapse,
  IconTimelineEvent,
  IconTimeline,
  IconAward,
  IconChecklist,
  IconSubtask,
  IconReportSearch,
  IconLayoutDashboard,
  IconMessageCircle,
  IconUser,
  IconTemplate
} from '@tabler/icons-react';
import QIconSrc from '../../assets/images/QIcon.svg';
import BrandLogo from '../ui/BrandLogo/BrandLogo';
import { ProjectDto } from '../../api/model';

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path?: string; // for router navigation
}

interface SidebarProps {
  // Project mode
  project?: ProjectDto;
  onBackToDashboard?: () => void;
  onSectionChange?: (sectionName: string) => void;
  
  // General mode
  useRouter?: boolean;
  customMenuItems?: MenuItem[];
  
  // Collapse state
  isCollapsed?: boolean;
  onCollapse?: () => void;
  
  // Active section (for project mode)
  activeSection?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  project,
  onBackToDashboard,
  onSectionChange,
  useRouter: useRouterNav = !project,
  customMenuItems,
  isCollapsed = false,
  onCollapse,
  activeSection = 'phase-plan-v2'
}) => {
  const router = useRouterNav ? useRouter() : null;
  const location = useRouterNav ? useLocation() : null;
  const { t } = useTranslation('navigation');

  const [internalActiveItem, setInternalActiveItem] = useState<string>('');
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(isCollapsed);

  // Determine if we in project
  const isProjectMode = !!project;

  // Default menu items for general sidebar
  const defaultMenuItems: MenuItem[] = [
    { id: 'dashboard', label: t('sidebar.dashboard'), icon: IconLayoutDashboard, path: '/dashboard' },
    { id: 'feedbacks', label: t('sidebar.feedbacks'), icon: IconMessageCircle, path: '/feedbacks' },
    { id: 'metrics', label: t('sidebar.metrics'), icon: IconUser, path: '/metrics' },
    { id: 'templates', label: t('sidebar.templates'), icon: IconTemplate, path: '/templates' },
  ];

  // Project menu items
  const projectMenuItems: MenuItem[] = [
    { id: 'phase-plan-v2', label: t('projectSideMenu.cockpit'), icon: IconTimeline },
    { id: 'deliverables', label: t('projectSideMenu.deliverables'), icon: IconTimelineEvent },
    { id: 'tasks', label: t('projectSideMenu.tasks'), icon: IconChecklist },
    { id: 'check-plan', label: t('projectSideMenu.checkPlan'), icon: IconAward },
    { id: 'task-action-points', label: t('projectSideMenu.actionPoints'), icon: IconSubtask },
    { id: 'report-metrics', label: t('projectSideMenu.reportMetrics'), icon: IconReportSearch },
  ];

  // Determine menu items to display
  const menuItems = customMenuItems || (isProjectMode ? projectMenuItems : defaultMenuItems);

  // Get active item from current pathname
  const getActiveItemFromPath = () => {
    if (!location) return 'dashboard';
    const pathname = location.pathname;
    if (pathname === '/dashboard') return 'dashboard';
    if (pathname === '/feedbacks') return 'feedbacks';
    if (pathname === '/metrics') return 'metrics';
    if (pathname === '/templates') return 'templates';
    return 'dashboard';
  };

  // Initialize active item based on mode
  useEffect(() => {
    if (useRouterNav && !isProjectMode) {
      setInternalActiveItem(getActiveItemFromPath());
    }
  }, [location?.pathname, useRouterNav, isProjectMode]);

  // Handle collapse toggle
  const handleToggleCollapse = () => {
    const newCollapsed = !internalIsCollapsed;
    setInternalIsCollapsed(newCollapsed);
    if (onCollapse) {
      onCollapse();
    }
  };

  // Handle menu item click
  const handleMenuClick = (item: MenuItem) => {
    setInternalActiveItem(item.id);

    if (isProjectMode) {
      if (onSectionChange) {
        onSectionChange(item.id);
      }
    } else if (useRouterNav && item.path && router) {
      router.navigate({ to: item.path as any });
    }
  };

  const sidebarClasses = internalIsCollapsed
    ? `${styles.sideMenu} ${styles.collapsed}`
    : styles.sideMenu;

  // Determine active state
  const currentActiveSection = isProjectMode ? activeSection : internalActiveItem;

  const projectName = project?.name || t('projectSideMenu.untitledProject');
  const projectDescription = project?.method || '';
  const projectIcon = project?.icon || QIconSrc;

  return (
    <div className={sidebarClasses}>
      <div className={styles.header}>
        {!internalIsCollapsed ? (
          <>
            <div className={styles.logoContainer}>
              <span className={styles.combinedLogo}>
                <BrandLogo className={styles.logoImage}/>
              </span>
            </div>
            <button className={styles.minimizeButton} onClick={handleToggleCollapse}>
              <IconLayoutSidebarLeftCollapse stroke={1} />
            </button>
          </>
        ) : (
          <>
            <button className={styles.minimizeButton} onClick={handleToggleCollapse}>
              <IconLayoutSidebarLeftCollapse stroke={1} />
            </button>
          </>
        )}
      </div>

      <div className={styles.menu}>
        {isProjectMode && !internalIsCollapsed && (
          <div className={styles.projectCard}>
            <img
              src={projectIcon}
              alt={projectName}
              className={styles.projectCardLogo}
            />
            <div className={styles.projectCardInfo}>
              <span className={styles.projectCardTitle}>{projectName}</span>
              <span className={styles.projectCardDescription}>
                {projectDescription}
              </span>
            </div>
          </div>
        )}

        <div className={styles.menuItems}>
          {menuItems.map((item) => (
            <div
              key={item.id}
              className={`${styles.menuItem} ${
                currentActiveSection === item.id ? styles.active : ''
              }`}
              onClick={() => handleMenuClick(item)}
            >
              <item.icon />
              {!internalIsCollapsed && (
                <span className={styles.menuItemText}>{item.label}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.profileWrapper}>
        <UserProfileDropdown isMinimized={internalIsCollapsed} />
      </div>
    </div>
  );
};

export default Sidebar;
